import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d202Api, FD120, FD121, FD122, FD123, FD126 } from '../../../services/d202Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useD202Mode';

export function useD202Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FD120 | null>(null);
  const [formData, setFormData] = useState<Partial<FD120>>({});
  const [details, setDetails] = useState<Partial<FD121>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by receipt no */
  const loadRecord = useCallback(async (receiptNo: string): Promise<{
    data: FD120;
    dets: Partial<FD121>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await d202Api.get(receiptNo);
      setLastApiResponse(data);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FD120, value: FD120[keyof FD120]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FD120): FD120[keyof FD120] => {
    return (formData as Record<string, FD120[keyof FD120]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FD121): FD121[keyof FD121] => {
    const sel = details[selectedRow] as Partial<FD121> | undefined;
    return sel ? (sel as Record<string, FD121[keyof FD121]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FD121, value: FD121[keyof FD121]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Collect all sub-detail arrays (colors, plates, sizes) from all details */
  const collectSubDetails = useCallback((): { colors: Partial<FD122>[]; plates: Partial<FD123>[]; sizes: Partial<FD126>[] } => {
    const colors: Partial<FD122>[] = [];
    const plates: Partial<FD123>[] = [];
    const sizes: Partial<FD126>[] = [];
    for (const d of details) {
      if (d.colors) colors.push(...d.colors);
      if ((d as FD121).colors) {
        for (const c of (d as FD121).colors || []) {
          if (c.plates) plates.push(...c.plates);
          if (c.sizes) sizes.push(...c.sizes);
        }
      }
    }
    return { colors, plates, sizes };
  }, [details]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FD120) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      const { colors, plates, sizes } = collectSubDetails();
      const req = {
        master: formData as FD120,
        details: details as FD121[],
        colors,
        plates,
        sizes,
        abnormals: [],
      };
      if (mode === 'add') {
        const result = await d202Api.create(req);
        setLastApiResponse(result);
        message.success(`新增成功: ${result.fd120_01}`);
        onSuccess(result);
      } else {
        const result = await d202Api.update(formData.fd120_01!, req);
        setLastApiResponse(result);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map((e: { message: string }) => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, collectSubDetails, clearFieldErrors, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await d202Api.delete(record.fd120_01);
      setLastApiResponse({ deleted: record.fd120_01 });
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fd121_02 || 0), 0);
    setDetails(prev => [...prev, { fd121_02: maxSeq + 1, fd121_01: formData.fd120_01 || '' }]);
  }, [details, formData.fd120_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fd121_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fd120_13: 1,       // 正常
      fd120_80: 'N',     // 未核准
      fd120_91: user?.userId || '',
      fd120_19: 0,
      fd120_16: 0,
      fd120_20: 0,
      fd120_201: 0,
      fd120_32: 0,
    });
    setDetails([{ fd121_02: 1 }]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  const importPurchaseOrder = useCallback(async (poNo: string) => {
    if (!poNo.trim()) return;
    setLoading(true);
    try {
      const preview = await d202Api.previewFromPurchaseOrder(poNo.trim());
      setLastApiResponse(preview);
      setFormData(prev => ({ ...prev, ...preview, fd120_01: prev.fd120_01 || '', fd120_21: poNo.trim() }));
      setDetails(preview.details || []);
      setSelectedRow(0);
      message.success(`已帶入採購單 ${poNo.trim()} 明細`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '採購單帶入失敗');
    } finally { setLoading(false); }
  }, []);

  const importInternalShipment = useCallback(async (shipmentNo: string) => {
    if (!shipmentNo.trim()) return;
    setLoading(true);
    try {
      const preview = await d202Api.previewFromInternalShipment(shipmentNo.trim());
      setLastApiResponse(preview);
      setFormData(prev => ({ ...prev, ...preview, fd120_01: prev.fd120_01 || '', fd120_46: shipmentNo.trim() }));
      setDetails(prev => [...prev.filter(d => d.fd121_10 || d.fd121_11), ...(preview.details || [])]);
      setSelectedRow(0);
      message.success(`已帶入內部交運單 ${shipmentNo.trim()} 明細`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '內部交運單帶入失敗');
    } finally { setLoading(false); }
  }, []);

  /** Apply result after save (for both add and edit) */
  const applyResult = useCallback((result: FD120) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    importPurchaseOrder, importInternalShipment,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
    lastApiResponse,
  };
}
